import AttachFileIcon from '@mui/icons-material/AttachFile'
import SendIcon from '@mui/icons-material/Send'
import { Box, Chip, CircularProgress, TextField, Tooltip, Typography } from '@mui/material'
import { DirectUpload } from '@rails/activestorage'
import { ChangeEvent, FC, KeyboardEvent, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import config from '../../../config'
import { useSendAiChatMessageMutation } from '../../../graphql/mutations/aiChat/__generated__/sendAiChatMessageMutation.generated'
import type { ChatMessageRole } from '../../../stores/chatStore'
import { useChatStore } from '../../../stores/chatStore'
import { useCurrentViewContext, useProfile } from '../../../utils/hooks'
import { IconButton } from '../../atoms'

const DIRECT_UPLOAD_URL = `${config.apollo.baseUrl}/direct_uploads`

// Muss mit Mutations::AiChat::SendMessage (Backend) übereinstimmen:
// MAX_ATTACHMENTS / MAX_ATTACHMENT_BYTES / ALLOWED_ATTACHMENT_TYPES.
const MAX_ATTACHMENTS = 5
const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024
const ALLOWED_ATTACHMENT_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
]
const ACCEPT_ATTRIBUTE = '.pdf,.jpg,.jpeg,.png,.docx,.xlsx'

interface PendingAttachment {
  key: string
  filename: string
  blobId?: string
  uploading: boolean
  error: boolean
}

interface AiChatInputProps {
  // Wird gesetzt, sobald der letzte Sendeversuch mit ai_chat.rateLimited
  // fehlgeschlagen ist — steuert, welche Fehlermeldung die MessageList zeigt.
  // Bewusst kein Store-Feld (siehe Task-7-Brief: chatStore ist der
  // verbindliche Kontrakt aus Task 6 und wird hier nicht erweitert).
  onRateLimitedChange: (rateLimited: boolean) => void
}

/**
 * Sende-Flow (siehe Task-7-Brief, Präzisierungen):
 * sendAiChatMessage({conversationId, content, viewContext, blobIds});
 * onCompleted setzt conversationId (falls neu), fügt die User-Message aus dem
 * Response-Payload in den Store ein und schaltet status auf 'waiting' — die
 * Assistant-Antwort kommt asynchron über useAiChatChannel.
 *
 * Anhänge laufen als ActiveStorage-DirectUpload (Muster: Uploader-Molecule)
 * sofort bei Auswahl hoch; die Mutation bekommt nur die signierten Blob-IDs.
 * Die Fall-Ablage (Notiz-Aktion) übernimmt das Backend, wenn der viewContext
 * einen zugreifbaren Vorgang enthält.
 */
const AiChatInput: FC<AiChatInputProps> = ({ onRateLimitedChange }) => {
  const { t } = useTranslation('ai')
  const [content, setContent] = useState('')
  const [attachments, setAttachments] = useState<PendingAttachment[]>([])
  const [attachmentsRejected, setAttachmentsRejected] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const conversationId = useChatStore(state => state.conversationId)
  const status = useChatStore(state => state.status)
  const selectedAgentId = useChatStore(state => state.selectedAgentId)
  const setConversationId = useChatStore(state => state.setConversationId)
  const addMessage = useChatStore(state => state.addMessage)
  const setStatus = useChatStore(state => state.setStatus)

  const viewContext = useCurrentViewContext()

  const { me } = useProfile()
  // Aktuell gültige Chat-Agenten des Users — Referenz für das Gaten von
  // selectedAgentId beim Senden (siehe unten). Persistiert im chatStore kann
  // eine stale ID überleben (Admin entzieht Agenten / leert dessen
  // hermine_agent_id); die darf nie ans Backend gehen (ai_chat.invalidAgent).
  const chatAgentIds = new Set(
    (me?.aiAgents ?? [])
      .filter((agent: { chatEnabled?: boolean }) => agent.chatEnabled)
      .map((agent: { id: string }) => agent.id)
  )

  const [sendAiChatMessage, { loading }] = useSendAiChatMessageMutation()

  const uploading = attachments.some(attachment => attachment.uploading)
  const disabled = status === 'waiting' || loading

  const updateAttachment = (key: string, patch: Partial<PendingAttachment>) => {
    setAttachments(current =>
      current.map(attachment => (attachment.key === key ? { ...attachment, ...patch } : attachment))
    )
  }

  const handleFilesSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    // Gleiche Datei erneut wählbar machen (Browser feuert onChange sonst nicht).
    event.target.value = ''
    if (files.length === 0) return

    setAttachmentsRejected(false)

    let accepted = attachments.length
    files.forEach(file => {
      const allowed =
        ALLOWED_ATTACHMENT_TYPES.includes(file.type) && file.size <= MAX_ATTACHMENT_BYTES
      if (!allowed || accepted >= MAX_ATTACHMENTS) {
        setAttachmentsRejected(true)
        return
      }
      accepted += 1

      const key = `${file.name}-${file.size}-${file.lastModified}`
      setAttachments(current => [
        ...current,
        { key, filename: file.name, uploading: true, error: false }
      ])

      const upload = new DirectUpload(file, DIRECT_UPLOAD_URL)
      upload.create((error: Error | null, blob: { signed_id?: string }) => {
        if (error || !blob?.signed_id) {
          updateAttachment(key, { uploading: false, error: true })
          return
        }
        updateAttachment(key, { uploading: false, blobId: blob.signed_id })
      })
    })
  }

  const removeAttachment = (key: string) => {
    setAttachments(current => current.filter(attachment => attachment.key !== key))
  }

  const handleSend = () => {
    const trimmedContent = content.trim()
    if (!trimmedContent || disabled || uploading) return

    const blobIds = attachments
      .filter(attachment => attachment.blobId)
      .map(attachment => attachment.blobId as string)

    onRateLimitedChange(false)
    setContent('')
    setAttachments([])
    setAttachmentsRejected(false)

    sendAiChatMessage({
      variables: {
        conversationId: conversationId ?? undefined,
        content: trimmedContent,
        viewContext,
        blobIds: blobIds.length > 0 ? blobIds : undefined,
        // Nur beim Start einer neuen Konversation mitsenden — bei laufender
        // Konversation ignoriert das Backend die ID ohnehin (Agent ist fix).
        // Zusätzlich gegen die aktuell gültigen Chat-Agenten gaten: eine
        // stale, persistierte selectedAgentId (Agent wurde dem User entzogen
        // oder dessen hermine_agent_id geleert) darf nie mitgesendet werden,
        // sonst wirft das Backend ai_chat.invalidAgent und die Nachricht geht
        // verloren. Ungültig → undefined → Standard-Agent.
        agentId:
          conversationId || !selectedAgentId || !chatAgentIds.has(selectedAgentId)
            ? undefined
            : selectedAgentId
      },
      onCompleted: data => {
        const payload = data.sendAiChatMessage
        if (!payload) return

        if (payload.conversation?.id) {
          setConversationId(payload.conversation.id)
        }

        if (payload.message?.id) {
          addMessage({
            id: payload.message.id,
            role: (payload.message.role as ChatMessageRole) ?? 'user',
            content: payload.message.content ?? '',
            status: payload.message.status,
            createdAt: payload.message.createdAt ?? undefined,
            uploads: (payload.message.uploads ?? []).map(upload => ({
              filename: upload.attachmentFilename ?? '',
              url: upload.attachmentUrl
            }))
          })
        }

        setStatus('waiting')
      },
      onError: error => {
        setStatus('error')
        onRateLimitedChange(error.message === 'ai_chat.rateLimited')
      }
    })
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSend()
    }
  }

  // Fall-Ablage passiert nur bei Vorgangs-Kontext — ohne Vorgang dezent darauf
  // hinweisen, dass die Datei ausschließlich an die KI geht.
  const showNoCaseHint = attachments.length > 0 && !viewContext.processingId

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
      {attachments.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {attachments.map(attachment => (
            <Chip
              key={attachment.key}
              size="small"
              color={attachment.error ? 'error' : 'default'}
              icon={attachment.uploading ? <CircularProgress size={14} /> : <AttachFileIcon />}
              label={attachment.filename}
              onDelete={() => removeAttachment(attachment.key)}
              // Lange Dateinamen ellipsieren statt die Bottom-Bar (und damit
              // den Drawer) aufzuziehen.
              sx={{ maxWidth: '100%' }}
            />
          ))}
        </Box>
      )}

      {attachmentsRejected && (
        <Typography variant="caption" color="error">
          {t('chat.attachInvalid')}
        </Typography>
      )}

      {showNoCaseHint && (
        <Typography variant="caption" color="text.secondary">
          {t('chat.attachNoCaseHint')}
        </Typography>
      )}

      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, width: '100%' }}>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          multiple
          accept={ACCEPT_ATTRIBUTE}
          onChange={handleFilesSelected}
        />
        <Tooltip title={t('chat.attach')}>
          <span>
            <IconButton
              disabled={disabled || attachments.length >= MAX_ATTACHMENTS}
              onClick={() => fileInputRef.current?.click()}
              icon={<AttachFileIcon />}
            />
          </span>
        </Tooltip>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          size="small"
          placeholder={t('chat.inputPlaceholder')}
          value={content}
          disabled={disabled}
          onChange={event => setContent(event.target.value)}
          onKeyDown={handleKeyDown}
        />
        <Tooltip title={t('chat.send')}>
          {/* span-Wrapper: Tooltip braucht ein Ref-fähiges Kind, IconButton
              leitet keine Refs durch — auch bei disabled bleibt der Tooltip so
              nutzbar. */}
          <span>
            <IconButton
              color="primary"
              disabled={disabled || uploading || !content.trim()}
              onClick={handleSend}
              icon={<SendIcon />}
            />
          </span>
        </Tooltip>
      </Box>
    </Box>
  )
}

export default AiChatInput
